import { useQuery, useZero } from "@/composables/useZero";
import { useDebounceFn } from "@vueuse/core";
import { ref, watch, type MaybeRefOrGetter, toValue } from "vue";
import { mutators, queries } from "@loanlink-nl/zero";

export function useDossierNotes(
	versionId: MaybeRefOrGetter<string | undefined>,
) {
	const z = useZero();

	// Query notes using Zero
	const { data: notesData, status } = useQuery(() => {
		const id = toValue(versionId);
		return queries.dossierVersionNotes.byVersionId({ versionId: id ?? "" });
	});

	const localNotes = ref<string | null>(null);
	const hasInitialized = ref(false);

	watch(
		() => notesData.value?.notes,
		(newVal) => {
			localNotes.value = newVal ?? null;
			hasInitialized.value = true;
		},
		{ immediate: true },
	);

	watch(
		() => toValue(versionId),
		() => {
			hasInitialized.value = false;
			localNotes.value = null;
		},
	);

	// Debounced save function
	const saveNotes = useDebounceFn(async (newNotes: string | null) => {
		const id = toValue(versionId);
		if (!id) return;

		z.value.mutate(
			mutators.dossierVersionNotes.upsert({
				versionId: id,
				notes: newNotes,
			}),
		);
	}, 1000);

	// Watch for local changes and auto-save
	watch(localNotes, (newVal, oldVal) => {
		// Only save if we have initialized and there's an actual change
		if (hasInitialized.value && oldVal !== undefined && newVal !== oldVal) {
			void saveNotes(newVal);
		}
	});

	return {
		notes: localNotes,
		status,
	};
}
